// Pure helpers for the per-customer account discount on REP field invoices.
// No React, no Supabase, no localStorage in this file so everything can be
// unit-tested. Nothing here reads or writes anything.
//
// Money model (decision D7). Items stay at CATALOG price. For an account
// discount of pct percent:
//   subtotal = round2(sum(price * qty))
//   amount   = round2(subtotal * pct / 100)
//   total    = round2(subtotal - amount)
// and the invoice carries  subtotal  and  discount = { pct, amount, label }
// (the same shape the dealer Checkout writes, so AdminInvoiceModal and the
// dealer invoice modal render it unchanged). The discount is a SNAPSHOT taken
// when the invoice is created; nothing recomputes an issued invoice from the
// customer's current rate.
//
// The same functions drive the live preview in the Create-invoice modal,
// createInvoice, the sync-retry backfill, the portal_orders payload and the
// tests, so the number the rep sees is the number saved.

export const DISCOUNT_LABEL = "Special discount";

// Shown (and used as the sync-pending reason) when portal_orders has no
// discount columns yet.
export const DISCOUNT_MIGRATION_MSG = "Database update 0034 has not been applied yet";

export const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

// Any value -> a safe percentage in 0..100 (max 4 decimals). Null, "", NaN,
// negative -> 0. Above 100 -> 100. Used wherever a rate is READ leniently
// (hydrating a row). The strict customer_settings reader refuses an
// out-of-range rate instead of clamping it (see getCustomerDiscountStrict).
export function normalizePct(v) {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return 0;
  if (n >= 100) return 100;
  return Math.round(n * 10000) / 10000;
}

// Two rates are "the same" when they agree to 4 decimals.
export function samePct(a, b) {
  return Math.abs(normalizePct(a) - normalizePct(b)) < 0.00005;
}

// "25" or "12.5" (no trailing zeros).
export function formatPct(v) {
  return String(normalizePct(v));
}

// total after an account discount of pct on subtotal (both already money).
export function discountedTotal(subtotal, pct) {
  const s = round2(subtotal);
  return round2(s - round2((s * normalizePct(pct)) / 100));
}

// items: [{ price, qty }]. Returns { subtotal, discount, total } where discount
// is null for a 0% customer (and then total === subtotal).
export function computePricing(items, pct) {
  const list = Array.isArray(items) ? items : [];
  const subtotal = round2(list.reduce((s, i) => s + (Number(i && i.price) || 0) * (Number(i && i.qty) || 0), 0));
  const p = normalizePct(pct);
  // No discount for a 0% customer, and none for an empty (or zero-value) basket: an empty invoice
  // must not be saved with a $0.00 discount block.
  if (p <= 0 || subtotal <= 0) return { subtotal, discount: null, total: subtotal };
  const amount = round2((subtotal * p) / 100);
  return { subtotal, discount: { pct: p, amount, label: DISCOUNT_LABEL }, total: round2(subtotal - amount) };
}

// The stored account rate of a portal_orders row (0 when the column is absent
// or NULL, i.e. every invoice created before migration 0034).
export function rowDiscountPct(row) {
  return normalizePct(row && row.discount_pct);
}

// portal_orders row -> invoice.discount ({ pct, amount, label }) or null.
export function discountFromRow(row) {
  const pct = rowDiscountPct(row);
  if (pct <= 0) return null;
  const hasAmount = row.discount_amount != null && row.discount_amount !== "" && Number.isFinite(Number(row.discount_amount));
  let amount;
  if (hasAmount) amount = round2(row.discount_amount);
  else if (row.subtotal != null && row.subtotal !== "" && Number.isFinite(Number(row.subtotal))) amount = round2((Number(row.subtotal) * pct) / 100);
  else return null;
  return { pct, amount, label: DISCOUNT_LABEL };
}

// The fields every row->invoice hydrator spreads into the cached invoice:
// {} for a row without a discount (so the invoice is EXACTLY what it was
// before this feature), otherwise { subtotal, discount }.
export function invoiceDiscountFields(row) {
  const discount = discountFromRow(row);
  if (!discount) return {};
  const hasSub = row.subtotal != null && row.subtotal !== "" && Number.isFinite(Number(row.subtotal));
  const subtotal = hasSub ? round2(row.subtotal) : round2((Number(row.total) || 0) + discount.amount);
  return { subtotal, discount };
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const isUuid = (v) => typeof v === "string" && UUID_RE.test(v.trim());

// Decision D4: identify the dealer ACCOUNT by uid only, never by company name
// or any fuzzy match. A route customer carries the dealer's profiles.id as
// userId / supabaseId / user_id (legacy shapes) or, for a dealer the admin
// assigned to the rep, as _supabaseKey (rep_route_customers.customer_key) with
// _supabaseSource === "admin_assign". Anything else (a rep-typed customer, a
// prospect, a "local-N" key) has NO linked dealer account and gets no
// automatic discount. The result must be UUID-shaped because it is used as a
// customer_settings.user_id (uuid) filter value.
export function resolveDiscountKey(cust) {
  if (!cust || typeof cust !== "object") return null;
  const direct = cust.userId || cust.supabaseId || cust.user_id;
  if (direct) return isUuid(String(direct)) ? String(direct).trim() : null;
  if (cust._supabaseSource === "admin_assign" && cust._supabaseKey && isUuid(String(cust._supabaseKey))) return String(cust._supabaseKey).trim();
  return null;
}

// True when a Supabase error means "one of the three discount columns does not
// exist" (migration 0034 not run). Only meaningful for a payload that carried the
// discount. Deliberately narrow: an "undefined column" (42703) or "column not in the
// schema cache" (PGRST204) error counts ONLY when its text names discount_pct,
// discount_amount or subtotal, so an unrelated unknown column ('rep', ...) or a
// not-null violation on a pre-existing subtotal column (23502) is reported as the
// ordinary save failure it is, not as "update 0034 has not been applied".
const DISCOUNT_COLUMN_NAME_RE = /\b(discount_pct|discount_amount|subtotal)\b/i;
export function isMissingDiscountColumnError(err) {
  if (!err) return false;
  const code = String(err.code || "");
  if (code !== "42703" && code !== "PGRST204") return false;
  const text = [err.message, err.details, err.hint].filter(Boolean).map(String).join(" ");
  return DISCOUNT_COLUMN_NAME_RE.test(text);
}

// The most a rep waits for the strict rate read at create time.
export const DISCOUNT_READ_TIMEOUT_MS = 10000;

// readFn(key) -> Promise<{ok, ...}>. Resolves with the read's result, or with
// { ok:false, error:"timed out" } if it takes longer than ms, or with { ok:false, error } if it
// throws. Never rejects, never hangs, so the caller can always fail closed and release its lock.
export function readDiscountWithTimeout(readFn, key, ms) {
  const limit = Number.isFinite(Number(ms)) && Number(ms) > 0 ? Number(ms) : DISCOUNT_READ_TIMEOUT_MS;
  return new Promise((resolve) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      console.warn("[readDiscountWithTimeout] the customer discount read did not answer within", limit, "ms for", key, "- treated as a failed read (fail closed)");
      resolve({ ok: false, error: "timed out" });
    }, limit);
    const fail = (e) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      console.warn("[readDiscountWithTimeout] the customer discount read threw for", key, ":", e?.message || e);
      resolve({ ok: false, error: e?.message || "The customer's discount could not be read." });
    };
    let pending;
    try { pending = Promise.resolve(readFn(key)); }   // started synchronously, so a click starts the read at once
    catch (e) { fail(e); return; }
    pending.then((res) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(res && typeof res === "object" ? res : { ok: false, error: "The customer's discount could not be read." });
    }, fail);
  });
}

const isFieldInvoice = (inv) => !!inv && (!!inv.repCreated || String(inv.id || "").startsWith("INV-F"));

// What the rep's LegalInvoiceModal prints in its totals block. An invoice
// WITHOUT a stored account discount renders exactly as before this feature
// (Subtotal and Total due are both the items sum). A rep field invoice that
// carries one shows Subtotal, "{label} ({pct}%) -$amount" and Total due = the
// STORED total, the figure that is charged, commissioned and refunded.
export function legalInvoiceTotals(inv) {
  const items = Array.isArray(inv && inv.items) ? inv.items : [];
  const raw = items.reduce((s, i) => s + (i.price * i.qty), 0);
  const d = inv && inv.discount;
  const pct = normalizePct(d && d.pct);
  if (!d || !(pct > 0) || !isFieldInvoice(inv)) {
    return { hasDiscount: false, subtotal: raw, discountAmount: 0, discountPct: 0, label: "", totalDue: raw };
  }
  const subtotal = round2(raw);
  const amount = Number.isFinite(Number(d.amount)) && d.amount != null ? round2(d.amount) : round2((subtotal * pct) / 100);
  const stored = Number(inv.total);
  const totalDue = Number.isFinite(stored) && stored > 0 ? stored : round2(subtotal - amount);
  return { hasDiscount: true, subtotal, discountAmount: amount, discountPct: pct, label: String(d.label || DISCOUNT_LABEL), totalDue };
}
